import { capitalizeFirstLetter } from "../string_convert_helper";
import type { ReportStatusKey } from "./reportStatusOptions";

// Each status's colour, on the report pages and in the streams.
export const REPORT_STATUS_COLORS: Record<ReportStatusKey, string> = {
	in_review: "#FFAA33",
	in_progress: "#1CB8BE",
	final_review: "#824C00",
	ready_to_close: "#A855F7",
	closed: "#00FF75",
};

// A report closed as rejected: still "Closed", in this colour.
export const REPORT_STATUS_REJECTED_COLOR = "#FF4D4F";

// A status's colour in the streams: a report closed as rejected in the
// rejected colour, as its page shows it.
export const getReportStatusColor = (
	key: ReportStatusKey,
	isRejected?: boolean
) =>
	key === "closed" && isRejected
		? REPORT_STATUS_REJECTED_COLOR
		: REPORT_STATUS_COLORS[key];

type OpenStatus =
	| "in_review"
	| "changes_required"
	| "in_progress"
	| "final_review"
	| "revision_required"
	| "ready_to_close";

interface ReportStatusDisplay {
	text: string;
	color: string;
}

export function getReportStatusDisplay(
	status?: string,
	openStatus?: string,
	isRejected?: boolean
): ReportStatusDisplay {
	const normalizedStatus = status?.toLowerCase();
	const normalizedOpenStatus = openStatus?.toLowerCase() as
		OpenStatus | undefined;

	if (normalizedStatus === "closed") {
		return {
			text: "Closed",
			color: isRejected
				? REPORT_STATUS_REJECTED_COLOR
				: REPORT_STATUS_COLORS.closed,
		};
	}

	if (normalizedStatus === "open") {
		switch (normalizedOpenStatus) {
			case "in_review":
				return {
					text: "Open - Initial review",
					color: REPORT_STATUS_COLORS.in_review,
				};
			case "final_review":
				return {
					text: "Open - Final review",
					color: REPORT_STATUS_COLORS.final_review,
				};
			case "ready_to_close":
				return {
					text: "Approved - Open",
					color: REPORT_STATUS_COLORS.ready_to_close,
				};
			case "in_progress":
			case "changes_required":
			case "revision_required":
				return {
					text: "Open - In progress",
					color: REPORT_STATUS_COLORS.in_progress,
				};
			default:
				return { text: "Open", color: REPORT_STATUS_COLORS.in_progress };
		}
	}

	// Any other status, such as a draft, as a word: "Draft".
	return {
		text: status ? capitalizeFirstLetter(status) : "",
		color: "#1CB8BE",
	};
}
